/**
 * O município da busca no mapa: o resto do mapa escurecido de leve (o
 * município "recortado", como no filtro dos focos — `fires/areaFocusLayer.ts`)
 * e o contorno branco com casco escuro, que se lê sobre a nuvem e sobre a
 * noite e não se confunde com o laranja do filtro dos focos. E, quando a
 * pessoa pediu a localização, um ponto azul onde ela está. Classe de canvas
 * própria: o recorte não pode apagar outras camadas. A exportação captura o canvas.
 */
import Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import type OlMap from 'ol/Map';
import type RenderEvent from 'ol/render/Event';
import VectorSource from 'ol/source/Vector';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import type { Coordinate } from 'ol/coordinate';
import { Z } from '../map/zIndex';
import { completarNome, enquadrarMunicipio, geometriasDo } from './municipios';
import { useBuscaStore } from './store';

/** Escurecimento fora do município: mais leve que o do filtro dos focos (0,5) — é destaque, não filtro. */
export const ESCURECIDO = 'rgba(7, 11, 20, 0.32)';

function tracar(ctx: CanvasRenderingContext2D, aneis: Coordinate[][]): void {
  for (const anel of aneis) {
    if (!anel.length) continue;
    ctx.moveTo(anel[0]![0]!, anel[0]![1]!);
    for (let i = 1; i < anel.length; i++) ctx.lineTo(anel[i]![0]!, anel[i]![1]!);
    ctx.closePath();
  }
}

const recorte = new Style({
  zIndex: 0,
  renderer: (coords, state) => {
    const ctx = state.context;
    const tipo = state.geometry.getType();
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = '#000';
    ctx.beginPath();
    if (tipo === 'Polygon') tracar(ctx, coords as Coordinate[][]);
    else if (tipo === 'MultiPolygon') for (const p of coords as Coordinate[][][]) tracar(ctx, p);
    ctx.fill('evenodd');
    ctx.restore();
  },
});

const CONTORNO = [
  recorte,
  new Style({ zIndex: 1, stroke: new Stroke({ color: 'rgba(0, 0, 0, 0.6)', width: 4.5, lineJoin: 'round' }) }),
  new Style({ zIndex: 2, stroke: new Stroke({ color: '#ffffff', width: 2, lineJoin: 'round' }) }),
];

const LOCAL = [
  new Style({ image: new CircleStyle({ radius: 14, fill: new Fill({ color: 'rgba(59, 130, 246, 0.22)' }) }) }),
  new Style({ image: new CircleStyle({ radius: 6, fill: new Fill({ color: '#3b82f6' }), stroke: new Stroke({ color: '#ffffff', width: 2 }) }) }),
];

export class BuscaLayer {
  private readonly fonte = new VectorSource<Feature<Geometry>>();
  private readonly camada: VectorLayer<VectorSource<Feature<Geometry>>>;
  private readonly fonteLocal = new VectorSource<Feature<Geometry>>();
  private readonly camadaLocal: VectorLayer<VectorSource<Feature<Geometry>>>;
  private readonly parar: () => void;
  private pedido = 0;

  constructor(private readonly map: OlMap) {
    this.camada = new VectorLayer({
      source: this.fonte,
      style: CONTORNO,
      zIndex: Z.BUSCA,
      className: 'ol-layer amaview-busca',
      properties: { id: 'busca', title: 'Município da busca' },
      updateWhileAnimating: true,
      updateWhileInteracting: true,
      // Sem município, escondida: o retângulo escuro não pode ficar sem recorte.
      visible: false,
    });
    // O retângulo escuro, antes do recorte das feições.
    this.camada.on('prerender', (e: RenderEvent) => {
      const ctx = e.context as CanvasRenderingContext2D | null;
      if (!ctx) return;
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = ESCURECIDO;
      ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.restore();
    });
    this.camadaLocal = new VectorLayer({
      source: this.fonteLocal,
      style: LOCAL,
      zIndex: Z.BUSCA_LOCAL,
      className: 'ol-layer amaview-busca-local',
      properties: { id: 'busca-local', title: 'Sua localização' },
    });
    map.addLayer(this.camada);
    map.addLayer(this.camadaLocal);
    this.parar = useBuscaStore.subscribe((a, b) => {
      if (a.municipio?.cd !== b.municipio?.cd) void this.mostrar();
      if (a.local !== b.local) this.mostrarLocal();
    });
    void this.mostrar();
    this.mostrarLocal();
  }

  private async mostrar(): Promise<void> {
    const n = ++this.pedido;
    const st = useBuscaStore.getState();
    const m = st.municipio;
    if (!m) {
      this.fonte.clear();
      this.camada.setVisible(false);
      return;
    }
    // Veio pelo link: o nome chega com o índice, e o enquadramento quando os limites chegarem.
    if (!m.nome) void completarNome(m.cd).catch(() => {});
    try {
      const geometrias = await geometriasDo(m.cd);
      if (n !== this.pedido) return;
      this.fonte.clear();
      this.fonte.addFeatures(geometrias.map((g) => new Feature(g)));
      this.camada.setVisible(geometrias.length > 0);
      if (st.enquadrarAoCarregar) {
        useBuscaStore.setState({ enquadrarAoCarregar: false });
        await enquadrarMunicipio(m.cd);
      }
    } catch (e) {
      console.warn('[AmaView] Busca: município', m.cd, 'não carregou:', e);
      if (n === this.pedido) {
        this.fonte.clear();
        this.camada.setVisible(false);
      }
    }
  }

  private mostrarLocal(): void {
    const local = useBuscaStore.getState().local;
    this.fonteLocal.clear();
    if (local) this.fonteLocal.addFeature(new Feature(new Point(local)));
  }

  dispose(): void {
    this.parar();
    this.map.removeLayer(this.camada);
    this.map.removeLayer(this.camadaLocal);
  }
}
